import { Yoda } from '@netoxygen/yoda';
import Config from '../config/Config';
import store from '../redux';

export const YODA_CLIENT_ERROR_SOURCE = 'kronos';
const BODY_SNIPPET_MAX = 4000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

/**
 * Collapse dynamic id segments so fingerprints group similar failures.
 *
 * @param {string} [path]
 * @returns {string}
 */
export const normalize_api_path = (path) => {
    if (!path)
        return '';

    return String(path)
        .split('?')[0]
        .split('/')
        .map((segment) => {
            if (!segment)
                return segment;
            if (UUID_RE.test(segment) || OBJECT_ID_RE.test(segment) || /^\d+$/.test(segment))
                return ':id';
            return segment;
        })
        .join('/');
};

/**
 * @param {{ method?: string, path?: string, status?: number|string, instance?: string }} ctx
 * @returns {string}
 */
export const build_client_error_fingerprint = (ctx = {}) => ([
    String(ctx.method || 'GET').toUpperCase(),
    normalize_api_path(ctx.path),
    ctx.status != null && ctx.status !== '' ? String(ctx.status) : '',
    ctx.instance || '',
].join('|'));

/**
 * @param {object} [user]
 * @returns {string}
 */
export const format_report_user = (user) => {
    if (!user || typeof user !== 'object')
        return '';

    const name = [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
    const parts = [
        name || null,
        user.email || null,
        user._id || user.id || null,
    ].filter(Boolean);

    return parts.join(' / ');
};

/**
 * Report an unexpected Kronos API error to Yoda. Returns slugLabel (Bxxxx) or null.
 *
 * @param {object} [ctx]
 * @returns {Promise<string|null>}
 */
export const report_kronos_client_error = async (ctx = {}) => {
    const project = Config.yoda_project_id;
    if (!project)
        return null;

    const method = String(ctx.method || 'GET').toUpperCase();
    const path = ctx.path || '';
    const normalized = normalize_api_path(path) || path || '?';
    const status = ctx.status;
    const instance = ctx.instance || '';
    const fingerprint = build_client_error_fingerprint({
        method,
        path,
        status,
        instance,
    });

    const title = `Kronos: ${method} ${normalized} → ${status ?? '?'} (${instance || 'no-instance'})`;
    const page = typeof window !== 'undefined' ? window.location.href : '';
    const bodySnippet = ctx.bodySnippet
        ? String(ctx.bodySnippet).slice(0, BODY_SNIPPET_MAX)
        : '';
    const description = [
        `requestId: ${ctx.requestId || ''}`,
        `page: ${page}`,
        `method: ${method}`,
        `path: ${path}`,
        `status: ${status ?? ''}`,
        `instance: ${instance}`,
        bodySnippet ? `body:\n${bodySnippet}` : '',
    ].filter(Boolean).join('\n');

    const user = store.getState()?.auth?.user;
    const yoda = new Yoda();
    try {
        const result = await yoda.reportClientError({
            project,
            source: YODA_CLIENT_ERROR_SOURCE,
            fingerprint,
            title,
            description,
            occurrence: {
                page: page || undefined,
                user: format_report_user(user) || undefined,
                ua: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                requestId: ctx.requestId || undefined,
                method,
                path: path || undefined,
                status: status != null && status !== '' ? Number(status) : undefined,
                instance: instance || undefined,
                bodySnippet: bodySnippet || undefined,
                appVersion: Config.app_version || undefined,
            },
        }, { timeout: 3000 });

        if (result?.slugLabel)
            return result.slugLabel;
        return null;
    } catch (_err) {
        return null;
    }
};
